import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Check, Phone } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { LeadForm } from "../components/LeadForm";
import { Map } from "../components/Map";
import { SEO } from "../components/SEO";
import { ReviewsMarquee } from "../components/ReviewsMarquee";
import { SITE, IMG, CITIES } from "../data/siteData";

const programCards = [
  { title: "Board & Train", desc: "Immersive 2-6 week programs for obedience, off-leash reliability and behaviour modification.", to: "/services/board-and-train", img: IMG.boardTrain },
  { title: "Private Training", desc: "One-on-one lessons customized to you and your dog's specific goals.", to: "/services/private-training", img: IMG.privateTraining },
  { title: "Jumpstart Program", desc: "A 2-week foundation of on-leash communication, manners and socialization for young dogs.", to: "/services/jumpstart", img: IMG.jumpstart },
];

const highlights = [
  "Certified professional trainers",
  "Behaviour modification specialists",
  "Owner education & go-home coaching included",
  "Lifetime support after your program",
  "Indoor facility + outdoor acreage",
  "10% veteran & first responder discount",
];

export default function CityLanding() {
  const { citySlug } = useParams();
  const city = CITIES.find((c) => c.slug === citySlug);
  if (!city) return <Navigate to="/dog-training/calgary" replace />;
  const others = CITIES.filter((c) => c.slug !== city.slug);

  return (
    <div data-testid={`city-page-${city.slug}`}>
      <SEO
        title={`Dog Training ${city.name}, AB | Chinook K9 Board & Train`}
        description={`Professional dog training for ${city.name}, Alberta. Board & train programs, private lessons and puppy training minutes from ${city.name}. Call 403-715-0499.`}
        path={`/dog-training/${city.slug}`}
      />

      <section className="relative min-h-[75vh] flex items-end overflow-hidden" data-testid="city-hero">
        <img src={city.hero} alt={`Dog training in ${city.name}, Alberta`} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-black/25 to-black/10" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-20 pt-44 w-full">
          <Reveal>
            <p className="text-brand font-heading font-bold tracking-[0.3em] text-xs uppercase mb-4 hero-text-shadow">Chinook K9 · Calgary & Area</p>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tighter text-white max-w-3xl leading-[1.05] hero-text-shadow">
              Professional Dog Training in {city.name}, Alberta!
            </h1>
            <p className="mt-5 text-white/85 max-w-2xl leading-relaxed text-base sm:text-lg hero-text-shadow">{city.hook}</p>
            <div className="mt-8 flex flex-wrap justify-center md:justify-start gap-4">
              <Link to="/services/board-and-train" data-testid="city-hero-cta" className="bg-brand text-white font-heading font-bold px-8 py-4 rounded-full hover:bg-red-700 transition-colors shadow-[0_0_25px_rgba(220,38,38,0.5)] inline-flex items-center gap-2">
                Board & Train! <ArrowRight size={18} />
              </Link>
              <a href={SITE.phoneHref} data-testid="city-hero-phone" className="border border-white/30 text-white font-heading font-semibold px-8 py-4 rounded-full hover:bg-white/10 transition-colors inline-flex items-center gap-2 backdrop-blur-sm">
                <Phone size={17} /> {SITE.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-24 text-slate-900" data-testid="city-intro">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <p className="text-brand font-heading font-bold tracking-[0.25em] text-xs uppercase mb-4">Dog Training {city.name}</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-6">
              {city.name}'s trusted dog training experts
            </h2>
            <p className="text-slate-600 leading-relaxed">{city.intro}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="bg-obsidian rounded-2xl p-8">
              <h3 className="font-heading text-lg font-bold text-white mb-5">Why {city.name} chooses Chinook K9</h3>
              <ul className="space-y-3">
                {highlights.map((h) => (
                  <li key={h} className="flex gap-2.5 text-sm text-white/75">
                    <Check size={16} className="text-brand shrink-0 mt-0.5" /> {h}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-obsidian py-24 border-y border-white/10" data-testid="city-programs">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white">
              Training programs for {city.name} dogs
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {programCards.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.1}>
                <Link to={p.to} data-testid={`city-program-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="group block relative rounded-2xl overflow-hidden h-[320px] border border-white/10">
                  <img src={p.img} alt={`${p.title} near ${city.name}`} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
                  <div className="absolute bottom-0 p-7">
                    <h3 className="font-heading text-xl font-bold text-white mb-2">{p.title}</h3>
                    <p className="text-white/70 text-sm leading-relaxed line-clamp-2">{p.desc}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <ReviewsMarquee />

      <section className="bg-obsidian py-24" data-testid="city-form-section">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal className="mb-10 text-center">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white">
              Book your {city.name} dog's assessment
            </h2>
            <p className="mt-4 text-white/60">Tell us about your dog and we'll recommend the right program.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <LeadForm compact />
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-24" data-testid="city-map-section">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="mb-10">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Minutes from {city.name}
            </h2>
            <p className="mt-3 text-slate-600">{SITE.address} · {SITE.hours}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <Map height={420} />
          </Reveal>
          <Reveal delay={0.15} className="mt-12">
            <p className="text-sm font-heading font-bold tracking-widest uppercase text-slate-400 mb-4">Also serving</p>
            <div className="flex flex-wrap gap-3">
              {others.map((c) => (
                <Link key={c.slug} to={`/dog-training/${c.slug}`} data-testid={`city-nearby-${c.slug}`}
                  className="border border-black/15 rounded-full px-5 py-2 text-sm font-medium text-slate-700 hover:border-brand hover:text-brand transition-colors">
                  Dog Training {c.name}
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
